import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChatCircleText, Lightning, ArrowRight } from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

/**
 * SMSPreferencesCard — shown on the trader dashboard.
 * - Priority traders: toggle SMS alerts on/off, show "You've hit your STOP" if opted out
 * - Standard traders: soft upsell to Priority tier
 */
export default function SMSPreferencesCard({ user }) {
  const [prefs, setPrefs] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const { data } = await api.get("/sms/preferences");
        setPrefs(data);
      } catch (e) {
        // Silent — endpoint returns 401 if not logged in, which shouldn't happen here
      }
    })();
  }, [user]);

  const tier = prefs?.subscription_tier || user?.subscription_tier || "standard";
  const isPriority = tier === "priority";

  const toggle = async (next) => {
    setBusy(true);
    try {
      const { data } = await api.put("/sms/preferences", { sms_alerts_enabled: next });
      setPrefs((p) => ({ ...(p || {}), sms_alerts_enabled: !!data.sms_alerts_enabled }));
      toast.success(next ? "SMS alerts on" : "SMS alerts paused");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  if (!prefs) return null;

  if (!isPriority) {
    // Upsell card for Standard traders
    return (
      <div
        className="brand-card p-5 relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #11523B 0%, #0d3f2d 100%)", color: "#fff" }}
        data-testid="sms-upsell-card"
      >
        <div className="flex items-start gap-4 flex-wrap">
          <div className="w-11 h-11 rounded-full bg-[var(--brand-orange)]/20 flex items-center justify-center flex-shrink-0">
            <Lightning weight="fill" size={22} className="text-[var(--brand-orange)]" />
          </div>
          <div className="flex-1 min-w-[240px]">
            <div className="text-[10px] font-bold uppercase tracking-wider text-[var(--brand-orange)]">New · Priority tier</div>
            <p className="font-display font-bold text-lg mt-0.5">Win jobs before your competitors do</p>
            <p className="text-white/85 text-sm mt-1">
              Get an instant SMS the moment a matching job is posted near you. Reply first, win the job.
              <span className="block mt-1 text-white/70">Just £7.99/mo · Up to 100 SMS included · Cancel anytime.</span>
            </p>
          </div>
          <Link to="/subscribe" className="btn-accent flex-shrink-0" data-testid="sms-upgrade-cta">
            Upgrade to Priority <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
      </div>
    );
  }

  // Priority trader — toggle + status
  const enabled = prefs.sms_alerts_enabled !== false;
  const optedOut = !!prefs.sms_opted_out;

  return (
    <div className="brand-card p-5" data-testid="sms-priority-card">
      <div className="flex items-start gap-4 flex-wrap">
        <div className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "rgba(226,109,41,0.12)" }}>
          <ChatCircleText weight="fill" size={22} className="text-[var(--brand-orange)]" />
        </div>
        <div className="flex-1 min-w-[240px]">
          <div className="flex items-center gap-2">
            <div className="text-[10px] font-bold uppercase tracking-wider text-[var(--brand-orange)]">Priority active</div>
            <Lightning weight="fill" size={12} className="text-[var(--brand-orange)]" />
          </div>
          <p className="font-display font-bold text-lg text-[var(--brand-green)] mt-0.5">Instant SMS alerts</p>
          <p className="text-sm text-[var(--text-muted)] mt-1">
            You&apos;ll get an SMS the moment a matching job is posted within your work range.
            {optedOut && (
              <span className="block mt-1 text-[#8a3030] font-semibold">
                You&apos;ve opted out via STOP. Text START to your Twilio sender to re-enable, or contact support.
              </span>
            )}
          </p>
          <ul className="text-xs text-[var(--text-muted)] mt-2 space-y-0.5">
            <li>• Up to 100 SMS per month</li>
            <li>• Quiet hours 9pm–7am (nothing sent overnight)</li>
            <li>• Reply STOP to any message to opt out at any time</li>
          </ul>
        </div>
        <div className="flex-shrink-0">
          <label className="inline-flex items-center cursor-pointer">
            <span className="text-sm font-semibold text-[var(--brand-green)] mr-3">{enabled ? "On" : "Off"}</span>
            <input
              type="checkbox"
              className="sr-only peer"
              checked={enabled}
              disabled={busy || optedOut}
              onChange={(e) => toggle(e.target.checked)}
              data-testid="sms-alerts-toggle"
            />
            <div className="relative w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[var(--brand-orange)] peer-disabled:opacity-50"></div>
          </label>
        </div>
      </div>
    </div>
  );
}
